import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardNotificationStore, $profileFeatureStore } from '@lib/store';
import { useQuery, useRouteHook } from '@shared/hooks';
import { cn, DateUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import type { Notification } from '@shared/api/rest/dashboards';
import { Button, Modal, PageContainer, Table, TableCellType, Text } from '@shared/widgets';

export const AlertsPage = () => {
  const query = useQuery();
  const id = query.get('id');
  const { navigate } = useRouteHook();

  const { data: features } = useUnit($profileFeatureStore.store);
  const dashboardNotificationStore = useUnit($dashboardNotificationStore.store);

  const [openAlert, setOpenAlert] = useState<Notification | null>(null);

  const { t } = useTranslation('alerts');

  useEffect(() => {
    if (!features?.settings.viewNotifications) navigate(RoutePaths.dashboard.onNavigate());
    $dashboardNotificationStore.events.getAll();
  }, [features]);

  useEffect(() => {
    if (dashboardNotificationStore.loading || !dashboardNotificationStore.data.length || !id) return;
    const notification = dashboardNotificationStore.data.find((notification) => notification.alertGuid === id) || null;
    setOpenAlert(notification);
  }, [dashboardNotificationStore.loading, dashboardNotificationStore.data, id]);

  const readAll = () => $dashboardNotificationStore.events.markAllAsRead({ allNotifications: true });
  const handleAlertAction = useCallback((alert: Notification) => setOpenAlert(alert), [setOpenAlert]);
  const handleAlertClose = useCallback(() => setOpenAlert(null), [setOpenAlert]);

  const isUnread = useMemo(
    () => dashboardNotificationStore.data.length > 0 && dashboardNotificationStore.data.filter((notification) => !notification.isRead).length > 0,
    [dashboardNotificationStore.data],
  );
  const columns = useMemo(() => [t('table.columns.alerts'), t('table.columns.details'), t('table.columns.alertTime'), ''], [t]);

  if (!features?.settings.viewNotifications) return null;
  return (
    <PageContainer title={t('title')} back backText={t('backText')}>
      <div className='mb-6 flex justify-between'>
        <Text size='2xl'>{t('header.title')}</Text>
        <Button color='outlined' onClick={readAll} disabled={dashboardNotificationStore.loading || !isUnread}>
          {t('header.buttons.markAllRead')}
        </Button>
      </div>
      <div className={cn(PaletteUtils.isVcfcPalette && 'rounded-lg bg-white')}>
        <Table
          isLoading={dashboardNotificationStore.loading}
          isScrollable
          columns={columns}
          items={dashboardNotificationStore.data.map((x) => [
            {
              type: TableCellType.Custom,
              value: (
                <div className='flex items-center gap-2'>
                  {!x.isRead && <div className='my-auto h-2 w-2 rounded-full bg-green' />}
                  <Text>{x.alertTypeDisplayString}</Text>
                </div>
              ),
            },
            { type: TableCellType.Text, value: `${x.subject?.slice(0, 50)}...` },
            { type: TableCellType.Text, value: DateUtils.formatDate(x.createdOn, { withTime: true }) },
            {
              type: TableCellType.Custom,
              value: (
                <Button fullWidth color='outlined' onClick={() => handleAlertAction(x)}>
                  {t('table.buttons.viewDetails')}
                </Button>
              ),
            },
          ])}
        />
      </div>
      <Modal isOpen={!!openAlert} onClose={handleAlertClose} title={t('modals.details.title')} width='332px'>
        <Text className='whitespace-pre-line'>{openAlert?.text}</Text>
        <br />
        {openAlert?.actionUrl && (
          <Button fullWidth color='primary' to={openAlert?.actionUrl} onClick={handleAlertClose}>
            {openAlert?.actionText}
          </Button>
        )}
      </Modal>
    </PageContainer>
  );
};
